import React, { useEffect, useMemo, useState } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../Firebase/firebaseconfig';
import { Users, TrendingUp, MapPin, History, ArrowLeftRight } from 'lucide-react';
import './Analytics.css';

const RANGE_OPTIONS = [
  { key: 'daily', label: 'Daily', limit: 14 },
  { key: 'weekly', label: 'Weekly', limit: 12 },
  { key: 'monthly', label: 'Monthly', limit: 12 },
];

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function startOfWeek(d) { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }

const BUCKET_FN = { daily: startOfDay, weekly: startOfWeek, monthly: startOfMonth };
const BUCKET_FMT = {
  daily: d => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
  weekly: d => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
  monthly: d => d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
};

export default function Analytics() {
  const [allUsers, setAllUsers] = useState([]);
  const [oldCount, setOldCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(() => (window.innerWidth <= 640 ? 'weekly' : 'daily'));
  const [showOldData, setShowOldData] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [userInfoSnap, visitSnap] = await Promise.all([
          get(ref(database, 'user_info')),
          get(ref(database, 'user_visit')),
        ]);
        if (userInfoSnap.exists()) setAllUsers(Object.values(userInfoSnap.val()));
        if (visitSnap.exists()) {
          const v = visitSnap.val();
          setOldCount(v.Count || 0);
          setNewCount(v.New_Count || 0);
        }
      } catch (err) {
        console.error('Failed to load analytics:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Current view only counts visits recorded by the new tracking (Version 2);
  // old view shows everything exactly as it looked before, for comparison.
  const users = useMemo(
    () => (showOldData ? allUsers : allUsers.filter(u => u.Version === 2)),
    [allUsers, showOldData]
  );
  const totalVisits = showOldData ? oldCount : newCount;

  const chartData = useMemo(() => {
    if (users.length === 0) return [];
    const bucketFn = BUCKET_FN[range];
    const fmt = BUCKET_FMT[range];
    const buckets = new Map();
    users.forEach(u => {
      if (!u.Timestamp) return;
      const d = new Date(u.Timestamp);
      if (isNaN(d.getTime())) return;
      const key = bucketFn(d).getTime();
      buckets.set(key, (buckets.get(key) || 0) + 1);
    });
    const limit = RANGE_OPTIONS.find(r => r.key === range).limit;
    return Array.from(buckets.keys())
      .sort((a, b) => a - b)
      .slice(-limit)
      .map(k => ({ label: fmt(new Date(k)), value: buckets.get(k) }));
  }, [users, range]);

  const regionData = useMemo(() => {
    const counts = {};
    users.forEach(u => {
      const region = u.Region || 'Unknown';
      counts[region] = (counts[region] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [users]);

  const maxValue = Math.max(1, ...chartData.map(d => d.value));

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="admin-dash-spinner" />
      </div>
    );
  }

  return (
    <div className="analytics-wrap">
      {showOldData && (
        <div className="analytics-old-banner">
          <History size={14} /> Viewing legacy data (recorded before visit tracking was rebuilt)
        </div>
      )}

      <div className="analytics-stat-card">
        <div className="analytics-stat-icon"><Users size={22} /></div>
        <div>
          <div className="analytics-stat-label">Total Users Visited</div>
          <div className="analytics-stat-value">{totalVisits.toLocaleString('en-IN')}</div>
        </div>
      </div>

      <div className="analytics-chart-card">
        <div className="analytics-chart-header">
          <h3><TrendingUp size={16} /> Visitor Trend</h3>
          <div className="analytics-range-toggle">
            {RANGE_OPTIONS.map(r => (
              <button
                key={r.key}
                className={[range === r.key ? 'active' : '', r.key === 'daily' ? 'desktop-only-option' : ''].filter(Boolean).join(' ')}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {chartData.length === 0 ? (
          <div className="analytics-empty">No visit data yet.</div>
        ) : (
          <BarChart key={range + showOldData} data={chartData} max={maxValue} />
        )}
      </div>

      <div className="analytics-region-card">
        <h3><MapPin size={16} /> Visits by Region</h3>
        {regionData.length === 0 ? (
          <div className="analytics-empty">No region data yet.</div>
        ) : (
          <ul className="analytics-region-list">
            {regionData.map(([region, count], i) => {
              const pct = Math.max(4, Math.round((count / regionData[0][1]) * 100));
              return (
                <li key={region} style={{ '--i': i }}>
                  <span className="region-name" title={region}>{region}</span>
                  <div className="region-bar-track">
                    <div className="region-bar-fill" style={{ '--w': `${pct}%` }} />
                  </div>
                  <span className="region-count">{count}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <button className="analytics-toggle-btn" onClick={() => setShowOldData(o => !o)}>
        <ArrowLeftRight size={15} />
        {showOldData ? 'Show current data' : 'Show old data'}
      </button>
    </div>
  );
}

function BarChart({ data, max }) {
  return (
    <div className="bar-chart">
      {data.map((d, i) => (
        <div className="bar-chart-col" key={i}>
          <div className="bar-chart-bar-wrap">
            <div className="bar-chart-bar" style={{ '--h': `${(d.value / max) * 100}%`, '--i': i }}>
              <span className="bar-chart-value">{d.value}</span>
            </div>
          </div>
          <span className="bar-chart-label">{d.label}</span>
        </div>
      ))}
    </div>
  );
}
